import JRDefault from '../assets/JR_Pin_inactive.png';
import JRSelected from '../assets/JR_Pin_active.png';
import MFDefault from '../assets/MF_Pin_inactive.png';
import MFSelected from '../assets/MF_Pin_active.png';
export type StringMap = Record<string, string | number | boolean>;

export function objectToQueryString(params: StringMap = {}): string {
    let queryString = "";
    Object.keys(params).forEach((key) => {
        if (queryString.length !== 0) queryString += "&";
        queryString += key + "=" + params[key].toString();
    });
    return queryString;
}

/**
 * Decodes to a [latitude, longitude] coordinates array.
 *
 * @param {String} polylineString
 * @param {Number} precision
 * @returns {Array}
 *
 * @see https://github.com/mapbox/polyline/blob/master/src/polyline.js#L36
 */
export function decodePolyline(polylineString: string, precision = 5): number[][] {
    const coordinates = [];
    const factor = Math.pow(10, precision);
    let index, lat, lng, byte, shift, result, latitude_change, longitude_change;
    index = 0;
    lat = 0;
    lng = 0;

    while (index < polylineString.length) {
        byte = null;
        shift = 0;
        result = 0;

        do {
            byte = polylineString.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);

        latitude_change = ((result & 1) ? ~(result >> 1) : (result >> 1));

        shift = result = 0;

        do {
            byte = polylineString.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);

        longitude_change = ((result & 1) ? ~(result >> 1) : (result >> 1));

        lat += latitude_change;
        lng += longitude_change;

        coordinates.push([lat / factor, lng / factor]);
    }

    return coordinates;
}


export function debounce<T extends (...args: any[]) => ReturnType<T>>(
    callback: T,
    timeout: number
): ((...args: Parameters<T>) => void) {
    let timer: ReturnType<typeof setTimeout>

    return (...args: Parameters<T>) => {
        clearTimeout(timer)
        timer = setTimeout(() => {
            callback(...args)
        }, timeout)
    }
}


export function getTextWidth(text: string): number {
    const canvas: HTMLCanvasElement = document.getElementById("canvasMarkerWoosmap") as HTMLCanvasElement || document.createElement("canvas") as HTMLCanvasElement;
    canvas.id = "canvasMarkerWoosmap";
    const context: CanvasRenderingContext2D = canvas.getContext("2d", { willReadFrequently: true }) as CanvasRenderingContext2D;
    context.font = "bold 15px sans-serif";
    return context.measureText(text).width;
}

/**
 * Check if input is an Object
 */
export function isObject(input: unknown): input is Record<string, unknown> {
    return typeof input === 'object' && input !== null && !Array.isArray(input);
}

/**
 * Merge the source object into the target like `Object.assign` but recursively.
 * No override if key already exist in target.
 */
export function merge<T extends Record<string, any>, S extends Record<string, any>>(target: T, source: S): T & S;
export function merge<T extends any, S extends any>(target: T, source: S): T | S;
export function merge(target: unknown, source: unknown): any {
    if (!isObject(source) || !isObject(target)) {
        return target;
    }
    for (const key in source) {
        const targetProp = target[key];
        const sourceProp = source[key];

        if (isObject(sourceProp) && isObject(targetProp)) {
            merge(targetProp, sourceProp);
        } else if (!(key in target)) {
            target[key] = source[key];
        }
    }
    return target;
}

/**
 * replace all keys enclosed by curly braces (eg. {replace_str}) by given values
 */
export function replace(str: string, keyValues: Record<string, string>): string {
    let result = str;
    Object.keys(keyValues).forEach((key) => {
        result = result.replace(
            new RegExp('\\{\\s*' + key + '\\s*\\}', 'g'),
            keyValues[key]
        );
    });
    return result;
}

/**
 * Used to parse the query parameters from the URL.
 * @param url
 */
export function parseURLParameters(url: string): Record<string, string | number> | null {
    try {
        const searchParams = new URLSearchParams(url);
        const parameters: Record<string, string | number> = {};

        searchParams.forEach((value, key) => {
            parameters[key] = isNaN(Number(value)) ? decodeURIComponent(value) : Number(value);
        });

        return parameters;
    } catch (error) {
        console.error(`Error parsing URL parameters: ${error}`);
        return null;
    }
}

export function getArrowSymbol(direction: string): string {
    switch (direction) {
        case 'up':
            return `
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d="M12 4l-6 6h12z" fill="#333" />
        </svg>
      `;
        case 'down':
            return `
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d="M12 20l6-6H6z" fill="#333" />
        </svg>
      `;
        case 'left':
            return `
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d="M4 12l6-6v12z" fill="#333" />
        </svg>
      `;
        case 'right':
            return `
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d="M20 12l-6 6V6z" fill="#333" />
        </svg>
      `;
        default:
            return '';
    }
}
const markerIcons = {
    JR: {
        default: JRDefault,
        selected: JRSelected
    },
    MF: {
        default: MFDefault,
        selected: MFSelected
    }
}
export function getMarkerIcon(type: string, selected: boolean): woosmap.map.Icon {
    const icons = type == "JR" ? markerIcons.JR : markerIcons.MF;
    return {
        url: selected ? icons.selected : icons.default,
        scaledSize: {
            height: 42,
            width: 32,
        }
    };
}